import React from "react"
import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { Loader2 } from "lucide-react"
import logger from '@/utils/logger';

// Error boundary component for the toaster
class ToasterErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Toaster error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      // Return null to not render anything if there's an error
      return null
    }

    return this.props.children
  }
}

function ToasterContent() {
  const { toasts } = useToast()

  return (
    <>
      {toasts && toasts.map(function ({ id, title, description, action, variant, ...props }) {
        // Determine color classes based on variant
        let colorClass = "";
        if (variant === "destructive") {
          colorClass = "text-red-700";
        } else if (variant === "default") {
          colorClass = "text-emerald-700";
        } else if (variant === "loading") {
          colorClass = "text-gray-900";
        } else {
          colorClass = "text-emerald-700";
        }
        return (
          <Toast key={id} duration={variant === "loading" ? Infinity : 4000} {...props} className={`bg-white relative pointer-events-auto`}>
            <div className="flex items-center gap-3">
              {variant === "loading" && (
                <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
              )}
              <div className="grid gap-1">
                {title && <ToastTitle className={colorClass}>{title}</ToastTitle>}
                {description && (
                  <ToastDescription className={colorClass}>{description}</ToastDescription>
                )}
              </div>
            </div>
            {action}
            {variant !== "loading" && <ToastClose className="absolute top-2 right-2" />}
          </Toast>
        )
      })}
      <ToastViewport className="pointer-events-none" />
    </>
  )
}

export function Toaster() {
  return (
    <ToasterErrorBoundary>
      <React.Suspense fallback={null}>
        <ToastProvider swipeDirection="right">
          <ToasterContent />
        </ToastProvider>
      </React.Suspense>
    </ToasterErrorBoundary>
  )
}
